import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  ArrowRight, 
  SkipForward, 
  Sparkles, 
  CheckCircle,
  X,
  Home,
  AlertTriangle,
  Star,
  Heart,
  Zap
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import LoadingSpinner from '../LoadingSpinner';
import { useOnboarding } from '../../hooks/useOnboarding';
import { supabase } from '../../lib/supabase';

// Import onboarding steps
import OnboardingStep1_Welcome from './OnboardingStep1_Welcome';
import OnboardingStep2_CareerProfile from './OnboardingStep2_CareerProfile';
import OnboardingStep3_GoalsAndPreferences from './OnboardingStep3_GoalsAndPreferences';
import OnboardingStep4_Completion from './OnboardingStep4_Completion';

// Import types and hook
import type { OnboardingData } from './onboardingTypes';

interface OnboardingFlowProps {
  onComplete: () => void;
  onSkip?: () => void;
  onCancel?: () => void;
}

// Celebration Animation Component
const CelebrationAnimation: React.FC<{ onComplete: () => void }> = ({ onComplete }) => {
  useEffect(() => {
    const timer = window.setTimeout(onComplete, 3000);
    return () => window.clearTimeout(timer);
  }, [onComplete]);

  // Generate confetti particles
  const confettiParticles = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: -10,
    rotation: Math.random() * 360,
    color: ['#22D3EE', '#A855F7', '#10B981', '#F59E0B', '#EF4444'][Math.floor(Math.random() * 5)],
    size: Math.random() * 8 + 4,
    delay: Math.random() * 2
  }));

  return (
    <motion.div
      className="fixed inset-0 z-[10000] pointer-events-none overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Confetti Particles */}
      {confettiParticles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full"
          style={{
            left: `${particle.x}%`,
            backgroundColor: particle.color,
            width: particle.size,
            height: particle.size
          }}
          initial={{ y: -10, rotate: 0 }}
          animate={{ 
            y: window.innerHeight + 50, 
            rotate: particle.rotation 
          }}
          transition={{ 
            duration: 3, 
            delay: particle.delay,
            ease: "easeOut"
          }}
        />
      ))}
    </motion.div>
  );
};

const OnboardingFlow: React.FC<OnboardingFlowProps> = ({ onComplete, onSkip, onCancel }) => {
  const { t } = useTranslation();
  const {
    currentStep,
    totalSteps,
    data,
    isLoading,
    error,
    userPersona,
    nextStep,
    prevStep,
    updateData,
    submitOnboarding,
    getCurrentQuestionType,
    calculateProgress,
    isCurrentStepValid
  } = useOnboarding();

  const [showCelebration, setShowCelebration] = useState(false);
  const [dontAskAgain, setDontAskAgain] = useState(false);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const targetStep = parseInt(urlParams.get('step') || '1');
      
      if (targetStep >= 1 && targetStep <= totalSteps) {
        if (targetStep > currentStep) {
          nextStep();
        } else {
          prevStep();
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentStep, totalSteps, nextStep, prevStep]);

  // Handle onboarding completion
  const handleOnboardingComplete = () => {
    console.log('?? Onboarding completed, calling onComplete callback');
    
    // Clear any manual trigger flags
    sessionStorage.removeItem('manual_onboarding_trigger');
    
    // Show confetti first, then complete
    setShowCelebration(true);
    
    // Complete onboarding after confetti
    setTimeout(() => {
      onComplete();
    }, 3000);
  };

  // Handle step completion with persona-aware logic
  const handleStepNext = async () => {
    console.log('?? OnboardingFlow handleStepNext:', { currentStep, totalSteps });
    
    // 📊 Meta Pixel: Track Step Completion
    try {
      if (typeof window !== 'undefined' && window.fbq) {
        if (currentStep === 1) {
          window.fbq('trackCustom', 'Step1Completed');
          console.log('📊 Meta Pixel: Step1Completed event tracked');
        } else if (currentStep === totalSteps) {
          window.fbq('trackCustom', 'Step12Completed');
          console.log('📊 Meta Pixel: Step12Completed event tracked');
        }
      }
    } catch (fbqError) {
      console.warn('Failed to track Meta Pixel step event:', fbqError);
    }
    
    if (currentStep === totalSteps) {
      try {
        console.log('?? Submitting onboarding data...');
        await submitOnboarding();
        
        // Show celebration and complete
        handleOnboardingComplete();
      } catch (err) {
        console.error('Failed to complete onboarding:', err);
      }
    } else {
      console.log('?? Moving to next step');
      nextStep();
    }
  };

  // Handle skip entire onboarding
  const handleSkipOnboarding = async () => {
    console.log('⏭️ Skipping onboarding');
    
    // If "Don't Ask Again" is checked, permanently disable onboarding
    if (dontAskAgain) {
      try {
        // CRITICAL FIX: Set localStorage flag immediately for instant feedback
        // This works for BOTH OAuth and phone users
        localStorage.setItem('onboarding_permanently_disabled', 'true');
        
        // Try to get Supabase OAuth user
        const { data: { user } } = await supabase.auth.getUser();
        
        // Also check for phone auth session
        const phoneAuthSession = localStorage.getItem('phone_auth_session');
        let userId = user?.id;
        
        if (!userId && phoneAuthSession) {
          try {
            const phoneSession = JSON.parse(phoneAuthSession);
            userId = phoneSession.user_id;
            console.log('📱 Using phone auth user ID:', userId);
          } catch (e) {
            console.error('Error parsing phone session:', e);
          }
        }
        
        if (userId) {
          await supabase
            .from('user_preferences')
            .upsert({
              user_id: userId,
              onboarding_complete: true,
              onboarding_completed_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }, {
              onConflict: 'user_id'
            });
          
          console.log('✅ Onboarding permanently disabled (DB + localStorage)');
        } else {
          console.warn('⚠️ No user ID found, using localStorage only');
        }
      } catch (err) {
        console.error('Error disabling onboarding:', err);
        // Even if DB fails, localStorage flag is set
      }
    }
    
    handleOnboardingComplete();
  };

  // Handle cancel onboarding
  const handleCancelOnboarding = async () => {
    console.log('❌ Cancelling onboarding');
    
    // If "Don't Ask Again" is checked, permanently disable onboarding
    if (dontAskAgain) {
      try {
        // CRITICAL FIX: Set localStorage flag immediately for instant feedback
        // This works for BOTH OAuth and phone users
        localStorage.setItem('onboarding_permanently_disabled', 'true');
        
        // Try to get Supabase OAuth user
        const { data: { user } } = await supabase.auth.getUser();
        
        // Also check for phone auth session
        const phoneAuthSession = localStorage.getItem('phone_auth_session');
        let userId = user?.id;
        
        if (!userId && phoneAuthSession) {
          try {
            const phoneSession = JSON.parse(phoneAuthSession);
            userId = phoneSession.user_id;
            console.log('📱 Using phone auth user ID:', userId);
          } catch (e) {
            console.error('Error parsing phone session:', e);
          }
        }
        
        if (userId) {
          await supabase
            .from('user_preferences')
            .upsert({
              user_id: userId,
              onboarding_complete: true,
              onboarding_completed_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }, {
              onConflict: 'user_id'
            });
          
          console.log('✅ Onboarding permanently disabled (DB + localStorage)');
        } else {
          console.warn('⚠️ No user ID found, using localStorage only');
        }
      } catch (err) {
        console.error('Error disabling onboarding:', err);
        // Even if DB fails, localStorage flag is set
      }
    }
    
    handleOnboardingComplete();
  };

  // Handle escape key for accessibility
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCancelOnboarding();
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  // Get current step component with proper persona-driven routing
  const getCurrentStepComponent = () => {
    const questionType = getCurrentQuestionType();
    console.log('?? Getting step component for:', { questionType, currentStep });
    
    switch (questionType) {
      case 'welcome':
        return OnboardingStep1_Welcome;
      case 'industry':
      case 'experience':
        return OnboardingStep2_CareerProfile;
      case 'goals':
      case 'career_goals':
      case 'leadership_goals':
      case 'learning_goals':
      case 'executive_goals':
      case 'transition_goals':
      case 'return_goals':
      case 'entry_goals':
      case 'work_preferences':
      case 'discovery':
        return OnboardingStep3_GoalsAndPreferences;
      case 'completion':
        return OnboardingStep4_Completion;
      default:
        console.warn('?? Unknown question type, defaulting to welcome');
        return OnboardingStep1_Welcome;
    }
  };

  const CurrentStepComponent = getCurrentStepComponent();
  
  // CRITICAL: Add error boundary and loading states
  if (isLoading) {
    return (
      <div className="min-h-screen vscode-bg-primary flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="vscode-text-primary text-lg">{t('views.onboarding.settingUpProfile')}</p>
        </div>
      </div>
    );
  }
  
  // Don't show error dialog for validation-related errors (fields are optional)
  if (error && !error.includes('required fields')) {
    return (
      <div className="min-h-screen vscode-bg-primary flex items-center justify-center p-4">
        <div className="vscode-bg-secondary rounded-xl p-8 border border-red-500/30 max-w-md mx-auto">
          <h2 className="text-xl font-semibold vscode-text-primary mb-4">{t('views.onboarding.errorTitle')}</h2>
          <p className="text-red-300 mb-6">{error}</p>
          <div className="space-y-3">
            <button
              onClick={handleCancelOnboarding}
              className="w-full bg-[var(--vscode-primary)] hover:bg-[var(--vscode-primary-hover)] text-[var(--vscode-primary-text)] font-semibold py-3 px-4 rounded-lg transition-colors"
            >
              {t('views.onboarding.goToDashboard')}
            </button>
            <button
              onClick={() => window.location.reload()}
              className="w-full vscode-bg-tertiary hover:vscode-bg-hover vscode-text-primary font-semibold py-3 px-4 rounded-lg transition-colors"
            >
              {t('views.onboarding.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Main Onboarding Modal */}
      <div className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
        <motion.div
          className="vscode-bg-secondary rounded-2xl shadow-2xl border vscode-border w-full max-w-[95vw] sm:max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
        >
          {/* Header */}
          <div className="vscode-bg-secondary/95 backdrop-blur-sm border-b vscode-border p-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-cyan-400 to-purple-500 rounded-lg flex items-center justify-center shadow-lg">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold vscode-text-primary">{t('views.onboarding.setupTitle')}</h1>
                  <p className="vscode-text-tertiary text-xs">
                    {t(`views.onboarding.stepType.${getCurrentQuestionType()}`)}
                  </p>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleSkipOnboarding}
                  className="flex items-center space-x-1 min-h-[44px] px-3 py-1.5 vscode-text-tertiary hover:vscode-text-secondary transition-colors text-sm rounded-lg hover:vscode-bg-tertiary touch-manipulation"
                  title={t('views.onboarding.skipSetupTitle')}
                >
                  <SkipForward className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('views.onboarding.skip')}</span>
                </button>
                
                <button
                  onClick={handleCancelOnboarding}
                  className="vscode-text-tertiary hover:vscode-text-primary transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center p-1.5 rounded-lg hover:vscode-bg-tertiary touch-manipulation"
                  title={t('views.onboarding.exitSetupTitle')}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Compact Progress Bar */}
            <div className="mt-3">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs vscode-text-tertiary">
                  {t('views.onboarding.stepOf', { currentStep, totalSteps })}
                </span>
                <span className="text-xs text-cyan-400">
                  {t('views.onboarding.percentComplete', { progress: calculateProgress() })}
                </span>
              </div>
              <div className="w-full h-1.5 vscode-bg-tertiary rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-cyan-500 to-purple-500"
                  initial={{ width: '0%' }}
                  animate={{ width: `${calculateProgress()}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mx-4 mt-4">
              <motion.div
                className="bg-red-900/50 border border-red-700 rounded-lg p-3"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center">
                  <AlertTriangle className="w-4 h-4 text-red-400 mr-2" />
                  <span className="text-red-300 text-sm">{error}</span>
                </div>
              </motion.div>
            </div>
          )}

          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto p-6 min-h-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={`${currentStep}-${getCurrentQuestionType()}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {CurrentStepComponent ? (
                  <CurrentStepComponent
                    data={data}
                    onDataChange={updateData}
                    onNext={handleStepNext}
                    onBack={prevStep}
                    isFirstStep={currentStep === 1}
                    isLastStep={currentStep === totalSteps}
                    currentStep={currentStep}
                    totalSteps={totalSteps}
                    isLoading={isLoading}
                    userPersona={userPersona}
                    questionType={getCurrentQuestionType()}
                  />
                ) : (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="vscode-text-primary">{t('views.onboarding.loadingStep')}</p>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Compact Footer Navigation */}
          <div className="vscode-bg-secondary/95 backdrop-blur-sm border-t vscode-border p-4 flex-shrink-0">
            {/* Don't Ask Again Checkbox */}
            <div className="mb-3 flex items-center justify-center">
              <label className="flex items-center space-x-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={dontAskAgain}
                  onChange={(e) => setDontAskAgain(e.target.checked)}
                  className="w-4 h-4 rounded vscode-border vscode-bg-tertiary text-cyan-500 focus:ring-2 focus:ring-cyan-500 focus:ring-offset-0 cursor-pointer"
                />
                <span className="text-sm vscode-text-tertiary group-hover:vscode-text-secondary transition-colors">
                  {t('views.onboarding.dontAskAgain')}
                </span>
              </label>
            </div>

            <div className="flex items-center justify-between">
              {/* Previous Button */}
              <div>
                {currentStep > 1 ? (
                  <button
                    onClick={prevStep}
                    className="flex items-center space-x-2 min-h-[44px] px-4 py-2 vscode-bg-tertiary hover:vscode-bg-hover vscode-text-secondary rounded-lg transition-colors touch-manipulation"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>{t('views.onboarding.previous')}</span>
                  </button>
                ) : (
                  <button
                    onClick={handleCancelOnboarding}
                    className="flex items-center space-x-2 min-h-[44px] px-4 py-2 vscode-bg-tertiary hover:vscode-bg-hover vscode-text-secondary rounded-lg transition-colors touch-manipulation"
                  >
                    <Home className="w-4 h-4" />
                    <span>{t('views.onboarding.exit')}</span>
                  </button>
                )}
              </div>

              {/* Step Indicators */}
              <div className="flex items-center space-x-1">
                {Array.from({ length: totalSteps }, (_, i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      i + 1 === currentStep 
                        ? 'bg-cyan-400 w-6' 
                        : i + 1 < currentStep 
                          ? 'bg-green-400' 
                          : 'bg-gray-600'
                    }`}
                  />
                ))}
              </div>

              {/* Next/Complete Button */}
              <div>
                <button
                  onClick={handleStepNext}
                  disabled={isLoading || !isCurrentStepValid}
                  className="flex items-center space-x-2 min-h-[44px] px-6 py-2 bg-[var(--vscode-primary)] hover:bg-[var(--vscode-primary-hover)] text-[var(--vscode-primary-text)] font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                >
                  {isLoading ? (
                    <>
                      <LoadingSpinner size="xs" showText={false} />
                      <span>{t('views.onboarding.settingUp')}</span>
                    </>
                  ) : currentStep === totalSteps ? (
                    <>
                      <span>{t('views.onboarding.completeSetup')}</span>
                      <CheckCircle className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      <span>{t('views.onboarding.next')}</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
            
            {/* Validation Feedback */}
            {!isCurrentStepValid && currentStep > 1 && (
              <div className="mt-2 text-center">
                <p className="text-yellow-400 text-xs">
                  {t('views.onboarding.completeRequiredFields')}
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Celebration Animation */}
      <AnimatePresence>
        {showCelebration && <CelebrationAnimation onComplete={handleOnboardingComplete} />}
      </AnimatePresence>
    </>
  );
};

export default React.memo(OnboardingFlow);
